import { Fn }			from "ts-base/fn";
import { Endo }			from "ts-base/endo";
import { Is }			from "ts-base/is";
import { NotNull }		from "ts-base/notNull";
import { Predicate }	from "ts-base/predicate";
import { Maybe }		from "ts-base/maybe";
import { Validated }	from "ts-base/validated";
import { Either }		from "ts-base/either";
import { Zoom }			from "ts-base/zoom";

/** S is the container, T is the containee */
export class Zoomer<S, T> {
	static on<T>():Zoomer<T, T> {
		return new Zoomer<T, T>(Fn.identity);
	}

	static of<S, T>(mod:Fn<Endo<T>, Endo<S>>):Zoomer<S, T> {
		return new Zoomer(mod);
	}

	static contextual<S, T>(inner:Fn<S, Zoomer<S, T>>):Zoomer<S, T> {
		return Zoomer.of<S, T>(
			(tt:Endo<T>)	=>
			(s:S)			=>
			inner(s).mod(tt)(s)
		);
	}

	//-------------------------------------------------------------------------

	constructor(readonly mod:Fn<Endo<T>, Endo<S>>) {}

	//-------------------------------------------------------------------------

	/** nasty hack, avoid this */
	cast<U>():Zoomer<S, U> {
		// return this.concatZoom(zoom.cast<U,T>());
		return this as unknown as Zoomer<S, U>;
	}

	/** only modify a value when it matches some condition */
	when(cond:Predicate<T>):Zoomer<S, T> {
		return this.zoom(Zoom.when(cond));
	}

	/** only modify a value when it matches some condition */
	is<U extends T>(is:Is<T, U>):Zoomer<S, U> {
		return this.zoom(Zoom.is(is));
	}

	/** modify an object property at a given key */
	atKey<K extends keyof T>(key:K):Zoomer<S, T[K]> {
		return this.zoom(Zoom.atKey(key));
	}

	/** modify an array element at the given index */
	atIndexMut(index:number):T extends Array<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.atIndexMut(index));
	}

	/** modify an array element at the given index */
	atIndexRo(index:number):T extends ReadonlyArray<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.atIndexRo(index));
	}

	/** modify each element of an array */
	eachMut():T extends Array<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.eachMut());
	}

	/** modify each element of an array */
	eachRo():T extends ReadonlyArray<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.eachRo());
	}

	/** modify a value if it's not null */
	notNull():null extends T ? Zoomer<S, NotNull<T>> : never {
		return this.anyZoom(Zoom.notNull());
	}

	/** modify the Some of a Maybe */
	some():T extends Maybe<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.some());
	}

	/** modify the Valid of a Validated */
	valid<E>():T extends Validated<E, infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.valid());
	}

	/** modify the Right of an Either */
	right<E>():T extends Either<E, infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.right());
	}

	/** modify the Left of an Either */
	left<U>():T extends Either<infer E, U> ? Zoomer<S, E> : never {
		return this.anyZoom(Zoom.left());
	}

	/** modify array elements matching a condition */
	whereMut<U>(pred:Predicate<U>):T extends Array<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereMut(pred));
	}

	/** modify array elements matching a condition */
	whereRo<U>(pred:Predicate<U>):T extends ReadonlyArray<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereRo(pred));
	}

	/** modify array elements at matching indices */
	whereIndexMut(pred:Predicate<number>):T extends Array<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereIndexMut(pred));
	}

	/** modify array elements at matching indices */
	whereIndexRo(pred:Predicate<number>):T extends ReadonlyArray<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereIndexRo(pred));
	}

	/** modify array elements chosen by value, index and length */
	whereValueIndexLengthMut(cond:(item:T, index:number, length:number)=>boolean):T extends Array<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereValueIndexLengthMut(cond));
	}

	/** modify array elements chosen by value, index and length */
	whereValueIndexLengthRo(cond:(item:T, index:number, length:number)=>boolean):T extends ReadonlyArray<infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.whereValueIndexLengthRo(cond));
	}

	/** modify the value of a Map at a given key */
	mapAtKeyMut<K>(key:K):T extends Map<K, infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.mapAtKeyMut(key));
	}

	/** modify the value of a Map at a given key */
	mapAtKeyRo<K>(key:K):T extends ReadonlyMap<K, infer U> ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.mapAtKeyRo(key));
	}

	/** modify the possibly existing value of a Map at a given key */
	mapMaybeAtKeyMut<K>(key:K):T extends Map<K, infer U> ? Zoomer<S, Maybe<U>> : never {
		return this.anyZoom(Zoom.mapMaybeAtKeyMut(key));
	}

	/** modify the possibly existing value of a Map at a given key */
	mapMaybeAtKeyRo<K>(key:K):T extends ReadonlyMap<K, infer U> ? Zoomer<S, Maybe<U>> : never {
		return this.anyZoom(Zoom.mapMaybeAtKeyRo(key));
	}

	/** modify the membership of a given value in a Set */
	setAtValueMut<K>(value:K):T extends Set<K> ? Zoomer<S, boolean> : never {
		return this.anyZoom(Zoom.setAtValueMut(value));
	}

	/** modify the membership of a given value in a Set */
	setAtValueRo<K>(value:K):T extends ReadonlySet<K> ? Zoomer<S, boolean> : never {
		return this.anyZoom(Zoom.setAtValueRo(value));
	}

	/** modify only some fields of an object */
	partialObject<U extends object>():T extends U ? Zoomer<S, U> : never {
		return this.anyZoom(Zoom.partialObject());
	}

	//-------------------------------------------------------------------------

	/** execute sideeffects before the actual mutation */
	tapBefore(action:Fn<T, void>):Zoomer<S, T> {
		return Zoomer.of((tt:Endo<T>) =>
			this.mod(
				Fn.andThen(
					Fn.tap(action),
					tt,
				)
			)
		);
	}

	/** execute sideeffects before the actual mutation */
	tapAfter(action:Fn<T, void>):Zoomer<S, T> {
		return Zoomer.of((tt:Endo<T>) =>
			this.mod(
				Fn.andThen(
					tt,
					Fn.tap(action),
				)
			)
		);
	}

	//-------------------------------------------------------------------------

	/** forms a category with Zoomer.on */
	then<U>(that:Zoomer<T, U>):Zoomer<S, U> {
		return this.zoom(that.mod);
	}

	zoom<U>(that:Zoom<U, T>):Zoomer<S, U> {
		return Zoomer.of(Fn.compose(this.mod, that));
	}

	contextualThen<U>(inner:Fn<T, Zoomer<T, U>>):Zoomer<S, U> {
		return this.then(Zoomer.contextual(inner));
	}

	//-------------------------------------------------------------------------

	/** turn into a setter */
	set(value:T):Endo<S> {
		return this.mod(Fn.constant(value));
	}

	/** execute a sideeffect and don't change anything */
	execute(action:Fn<T, void>):Endo<S> {
		// since void never actually changes anything, it's irrelevant whether we tap the value before or after the "change"
		return this.tapAfter(action).void();
	}

	/** modifies where the modification is taking the original container value into account */
	contextMod(change:Fn<S, Endo<T>>):Endo<S> {
		return (s) => this.mod(change(s))(s);
	}

	/** sets where the new value is taking the original container value into account */
	contextSet(change:Fn<S, T>):Endo<S> {
		return (s) => this.set(change(s))(s);
	}

	/** throw away the change, useful in conjunction with watch */
	void():Endo<S> {
		return this.mod(Fn.identity);
	}

	//-------------------------------------------------------------------------

	// ugly hack to appease the type checker
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private anyZoom<U>(that:Zoom<U, any>):any {
		return Zoomer.of(Fn.compose(this.mod, that));
	}
}
